import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DialogResult, DialogService, FormNavigationService, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { POP_UP_CODES, QUESTION_PATHS } from '../../constants/app-data.constants';
import { SYNC_OPERATION_NAMES } from '../../constants/sync-operation.constants';
import { SYNC_MESSENGER_MESSAGE_TYPES } from '../../constants/messaging.constants';

import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';
import { GridFilterRequest } from '../../interfaces/grid-filter-request.interface';
import {
  BaseMessageRequest,
  BaseMessageProgress,
  BaseMessageResult,
  SyncRequestMessage,
  DiagnosticExportRequestMessage,
  DiagnosticExportStartedMessage,
  DiagnosticExportEndedMessage,
  DiagnosticExportResult,
  SyncEntityRequestMessage,
  SyncEntityStartedMessage,
  SyncEntityProgressMessage,
  SyncEntityEndedMessage,
  DiscardEntityRequestMessage,
  DiscardEntityStartedMessage,
  DiscardEntityProgressMessage,
  DiscardEntityEndedMessage
} from '../../interfaces/messaging.interface';

import { SyncMessenger } from '../../helpers/sync-messenger';
import { DiagnosticArchiver } from '../../helpers/diagnostic-archiver';
import { LogBuffer } from '../../helpers/log-buffer';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ExcelImportService } from '../../services/excel-import.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { AsyncDialogService } from '../../services/async-dialog.service';
import { NetworkConnectivityService } from '../../services/network-connectivity.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { SyncOperationService } from '../../services/sync-operation.service';
import { SyncMessengerService } from '../../services/sync-messenger.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-sync-entity-list',
  templateUrl: './sync-entity-list.component.html',
  styleUrls: ['./sync-entity-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SyncEntityListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private syncMessenger: SyncMessenger;
  private loggedInUserProfile: LoggedInUserProfile;

  activeDialog: string = '';

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    protected excelImportService: ExcelImportService,
    private syncOperationService: SyncOperationService,
    private asyncDialogService: AsyncDialogService,
    private networkConnectivityService: NetworkConnectivityService,
    syncMessengerService: SyncMessengerService) {
    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.syncEntityList,
      () => API_ENDPOINTS.local.getSyncEntities,
      true);

    this.syncMessenger = syncMessengerService.getSyncMessenger();

    this.syncMessenger.addListener<DiagnosticExportStartedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.diagnosticExportStarted, message => this.onDiagnosticExportStarted(message));
    this.syncMessenger.addListener<SyncEntityStartedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityStarted, message => this.onSyncEntityStarted(message));
    this.syncMessenger.addListener<DiscardEntityStartedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityStarted, message => this.onDiscardEntityStarted(message));

    this.syncMessenger.addListener<SyncEntityProgressMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityProgress, message => this.onSyncEntityProgress(message));
    this.syncMessenger.addListener<DiscardEntityProgressMessage>(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityProgress, message => this.onDiscardEntityProgress(message));

    this.syncMessenger.addListener<DiagnosticExportEndedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.diagnosticExportEnded, message => this.onDiagnosticExportEnded(message));
    this.syncMessenger.addListener<SyncEntityEndedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityEnded, message => this.onSyncEntityEnded(message));
    this.syncMessenger.addListener<DiscardEntityEndedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityEnded, message => this.onDiscardEntityEnded(message));

    this.syncOperationService.registerHandler(this.componentClassName);
  }

  ngOnInit(): void {

    const logPrefix = 'ngOnInit';
    this.logger.log(`${logPrefix}: enter`);

    this.ignoreCanDeactivateGuard = true;

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();
    this.waitForServiceWorker().then(waitResult => this.loggedInUserProfile = waitResult.loggedInUserProfile);

    this.logger.log(`${logPrefix}: leave`);
  }

  ngOnDestroy(): void {
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.diagnosticExportStarted);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityStarted);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityStarted);

    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityProgress);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityProgress);

    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.diagnosticExportEnded);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityEnded);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityEnded);

    this.syncOperationService.unregisterHandler(this.componentClassName);

    this.completeDestroy();
  }

  formLoad(event: any): void {

    const logPrefix = 'formLoad';
    this.logger.log(`${logPrefix}: enter`);

    this.configData = event.configData;
    this.loadGridDataOnFormLoad();

    this.logger.log(`${logPrefix}: leave`);
  }

  filterData(event: any): void {
    const filterRequest = this.getFilterRequest();
    this.loadGridData(filterRequest);
  }

  actionButton_syncEntityPlaceholder_datagrid_custom_startSync_Clicked(event: any) {
    this.sync();
  }

  actionButton_syncEntityPlaceholder_datagrid_custom_diagnosticExport_Clicked(event: any) {
    this.exportDiagnostics();
  }

  actionButton_syncEntityPlaceholder_datagrid_viewEntity_Clicked(event: any) {
    const entityRow = event.event.data;
    this.viewEntity(entityRow);
  }

  actionButton_syncEntityPlaceholder_datagrid_syncEntity_Clicked(event: any) {
    const entityRow = event.event.data;
    this.syncEntity(entityRow);
  }

  actionButton_syncEntityPlaceholder_datagrid_discardEntity_Clicked(event: any) {
    const entityRow = event.event.data;
    this.discardEntity(entityRow);
  }

  private getFilterRequest(): GridFilterRequest {
    const searchQuestionModel = this.getQuestionValue(this.model, ...QUESTION_PATHS.syncEntitylist.search);
    const filterRequest: GridFilterRequest = {
      searchTerm: searchQuestionModel
    };
    return filterRequest;
  }

  private sync(): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }
    this.syncMessenger.postMessage<SyncRequestMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncRequest, { requestSource: this.componentClassName, isStartup: false });
  }

  private exportDiagnostics(): void {
    this.syncMessenger.postMessage<DiagnosticExportRequestMessage>(SYNC_MESSENGER_MESSAGE_TYPES.diagnosticExportRequest, { requestSource: this.componentClassName });
  }

  private viewEntity(entityRow: any): void {
    const titlePlaceholders = { title: `Details of: ${entityRow.entityIdForDisplay}` };
    const textPlaceholders = { text: entityRow.entityDetails };
    this.dialogService.showPopup(POP_UP_CODES.syncEntityList.entityDetails, null, textPlaceholders, null, null, titlePlaceholders);
  }

  private syncEntity(entityRow: any): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }
    const { entityName, templateId, entityId } = entityRow;
    const requestSource = this.componentClassName;
    this.syncMessenger.postMessage<SyncEntityRequestMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncEntityRequest, { requestSource, entityName, templateId, entityId });
  }

  private async discardEntity(entityRow: any): Promise<void> {
    const { entityName, templateId, entityType, entityId, entityIdForDisplay } = entityRow;
    const dialogResult = await this.asyncDialogService.showConfirmPopup(POP_UP_CODES.syncEntityList.discardEntityConfirmation, { entityType, entityIdForDisplay });
    if (dialogResult === DialogResult.ACCEPTED) {
      const requestSource = this.componentClassName;
      this.syncMessenger.postMessage<DiscardEntityRequestMessage>(SYNC_MESSENGER_MESSAGE_TYPES.discardEntityRequest, { requestSource, entityName, templateId, entityId });
    }
  }

  private onDiagnosticExportStarted(message: DiagnosticExportStartedMessage): void {
    this.spinnerService.showSpinner();
  }

  private onSyncEntityStarted(message: SyncEntityStartedMessage): void {
    this.onSyncOperationStarted(SYNC_OPERATION_NAMES.syncEntity, message.request);
  }

  private onDiscardEntityStarted(message: DiscardEntityStartedMessage): void {
    this.onSyncOperationStarted(SYNC_OPERATION_NAMES.discardEntity, message.request);
  }

  private onSyncEntityProgress(message: SyncEntityProgressMessage): void {
    this.onSyncOperationProgress(SYNC_OPERATION_NAMES.syncEntity, message.request, message.progress);
  }

  private onDiscardEntityProgress(message: DiscardEntityProgressMessage): void {
    this.onSyncOperationProgress(SYNC_OPERATION_NAMES.discardEntity, message.request, message.progress);
  }

  private onDiagnosticExportEnded(message: DiagnosticExportEndedMessage): void {

    const onSuccess = (fileName: string) =>
      this.dialogService.showConfirmPopup(POP_UP_CODES.syncEntityList.exportDiagnosticsSuccess, { fileName });

    const onPartialSuccess = (fileName: string) =>
      this.dialogService.showConfirmPopup(POP_UP_CODES.syncEntityList.exportDiagnosticsPartialSuccess, { fileName });

    const onFailure = () =>
      this.dialogService.showConfirmPopup(POP_UP_CODES.syncEntityList.exportDiagnosticsFailure);

    this.downloadExportedDiagnosticsToFile(message.result)
      .then(result => !result.error ? onSuccess(result.zipFileName) : onPartialSuccess(result.zipFileName))
      .catch(error => {
        this.logger.error('Export diagnostics failed with error:', error);
        onFailure();
      })
      .finally(() => this.spinnerService.hideSpinner());
  }

  private onSyncEntityEnded(message: SyncEntityEndedMessage): void {
    this.onSyncOperationEnded(SYNC_OPERATION_NAMES.syncEntity, message.request, message.result);
  }

  private onDiscardEntityEnded(message: DiscardEntityEndedMessage): void {
    this.onSyncOperationEnded(SYNC_OPERATION_NAMES.discardEntity, message.request, message.result);
  }

  private onSyncOperationStarted<TRequest extends BaseMessageRequest>(
    operationName: string,
    request: TRequest):
    void {

    this.syncOperationService.onStarted(operationName, request);
  }

  private onSyncOperationProgress<TRequest extends BaseMessageRequest, TProgress extends BaseMessageProgress>(
    operationName: string,
    request: TRequest,
    progress: TProgress):
    void {

    this.syncOperationService.onProgress(operationName, request, progress);
  }

  private onSyncOperationEnded<TRequest extends BaseMessageRequest, TResult extends BaseMessageResult>(
    operationName: string,
    request: TRequest,
    result: TResult):
    void {

    this.syncOperationService.onEnded(operationName, request, result);
    this.filterData(null);
  }

  private async downloadExportedDiagnosticsToFile(diagnosticExportResult: DiagnosticExportResult): Promise<{ zipFileName: string, error: string }> {

    const [loggedInUserProfile, webSiteLogEntries, syncLogEntries] = [this.loggedInUserProfile, LogBuffer.getEntries(), this.syncOperationService.getSyncLogEntries()];

    const { zipFileName, zipBlob, error } = await DiagnosticArchiver.archiveDiagnostics(loggedInUserProfile, webSiteLogEntries, syncLogEntries, diagnosticExportResult);

    this.htmlHelperService.downloadBlob(zipBlob, zipFileName);

    return { zipFileName, error };
  }
}
